import React, { useCallback, useState } from 'react';

import { LoginOutcome, MfaChallenge } from 'config/axiosInstance';
import EmailCodeStep from './EmailCodeStep';
import PasswordChangeStep from './PasswordChangeStep';
import TotpSetupStep from './TotpSetupStep';
import TotpStep from './TotpStep';

type MfaFlowProps = {
  /** ログイン（POST /v1/web/token）の 202 で受け取ったチャレンジ。 */
  initial: MfaChallenge;
  email: string;
  /** パスワードの画面へ戻す。message は login の名前空間の鍵（無ければ null）。 */
  onRestart: (message: string | null) => void;
  /** ログインが済んだ（確かめも済み）。 */
  onSignedIn: () => void;
};

/** 確かめが済まなかったときの文（login の名前空間の鍵）。 */
const OUTCOME_MESSAGE: Record<Exclude<LoginOutcome, 'ok'>, string> = {
  not_manager: 'mfa.error-no-role',
  network: 'error_network',
  cookie_unavailable: 'error_cookie_unavailable',
};

/**
 * YAC-2026-009 PR5a。運営の2段階認証の流れ（サーバーの mfa の値で段を出し分ける）。
 *  1. 初めて: パスワードの変更 → 認証アプリの登録 → トークン
 *  2. 認証アプリが未登録: メールのコード → 認証アプリの登録 → トークン
 *  3. 2回目から: メールのコード → 認証アプリのコード → トークン
 *
 * チャレンジ（mfa_token）は段ごとに替わる。この部品の state（メモリ）だけに置き、
 * localStorage・sessionStorage・URL・履歴の state には置かない。パスワードの画面へ戻ると捨てる。
 */
const MfaFlow: React.FC<MfaFlowProps> = ({ initial, email, onRestart, onSignedIn }) => {
  const [challenge, setChallenge] = useState<MfaChallenge>(initial);

  const signedIn = useCallback(
    (outcome: LoginOutcome) => {
      if (outcome === 'ok') onSignedIn();
      else onRestart(OUTCOME_MESSAGE[outcome]);
    },
    [onRestart, onSignedIn]
  );

  const props = { challenge, email, onNext: setChallenge, onRestart, onSignedIn: signedIn };
  // 段が替わったら作り直す（入力・誤りの表示・待ちの秒数を前の段から持ち越さない）。
  const key = `${challenge.stage}:${challenge.token}`;
  switch (challenge.stage) {
    case 'password_change_required':
      return <PasswordChangeStep key={key} {...props} />;
    case 'email_code_required':
      return <EmailCodeStep key={challenge.stage} {...props} />;
    case 'setup_required':
      return <TotpSetupStep key={key} {...props} />;
    case 'totp_required':
      return <TotpStep key={key} {...props} />;
    default:
      return null;
  }
};

export default MfaFlow;
